// Admin → AI Management
//
// Tunes the AI Allocate algorithm. Two sections:
//
// 1. Scoring components — each contributes to the per-candidate score
//    on every auto-allocate run. Editable: enabled, weight, display
//    order. Up/down arrows reorder (display order is purely visual,
//    but reads as priority). Higher weight = stronger contribution.
//
// 2. Limits — structural rules: max refs/bench per game, conflict
//    window, affinity lookback, person-tier thresholds, trainee
//    exclusion + regex.
//
// All knobs persist to a single AI_ALLOCATE_CONFIG_v1 row in Settings
// (one PUT per Save). Reset wipes the row so the loader returns the
// baked-in defaults on the next auto-allocate POST.

import React, { useEffect, useState, useCallback } from 'react';
import { auditFetch } from '../auditFetch';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

export default function AdminAIManagement({ canEdit }) {
  const [config, setConfig] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(''); setSuccess(''); setDirty(false);
    try {
      const res = await fetch(`${API_BASE}/admin/ai-allocate-config`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setConfig(data.config);
      setDefaults(data.defaults);
    } catch (e) { setError(e.message || 'Failed to load config'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-clear success after 4s; errors stay until dismissed.
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 4000);
    return () => clearTimeout(t);
  }, [success]);

  const updateScoring = (idx, patch) => {
    setConfig(prev => {
      const next = { ...prev, scoring: prev.scoring.map((s, i) => i === idx ? { ...s, ...patch } : s) };
      return next;
    });
    setDirty(true);
  };

  // Move a scoring row up or down. The displayOrder field on each row
  // is renumbered after the swap so the saved order persists across
  // reloads. The algorithm itself doesn't care about order (it sums
  // every enabled rule), but the UI presents the list top-to-bottom
  // as priority.
  const moveScoring = (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= config.scoring.length) return;
    const arr = [...config.scoring];
    [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
    arr.forEach((s, i) => { s.displayOrder = i; });
    setConfig({ ...config, scoring: arr });
    setDirty(true);
  };

  const updateLimit = (key, value) => {
    setConfig(prev => ({ ...prev, limits: { ...prev.limits, [key]: value } }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const res = await auditFetch(`${API_BASE}/admin/ai-allocate-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setConfig(data.config);
      setSuccess('Config saved. Next AI Allocate run will use the new values.');
      setDirty(false);
    } catch (e) { setError(e.message || 'Failed to save'); }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!window.confirm('Reset every AI Allocate setting back to the baked-in defaults? This wipes the saved config; you can re-edit afterwards.')) return;
    setSaving(true); setError(''); setSuccess('');
    try {
      const res = await auditFetch(`${API_BASE}/admin/ai-allocate-config/reset`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setConfig(data.config);
      setSuccess('Reset to defaults.');
      setDirty(false);
    } catch (e) { setError(e.message || 'Failed to reset'); }
    setSaving(false);
  };

  if (loading || !config) return <div className="admin-section"><div className="loading">Loading AI config…</div></div>;

  // Sort scoring by displayOrder so the persisted order is honored on reload.
  const scoringSorted = [...config.scoring].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  // Map back to original indices for the moveScoring/updateScoring
  // callbacks (so they can splice the underlying array).
  const indexInOriginal = (key) => config.scoring.findIndex(s => s.key === key);

  return (
    <div className="admin-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>AI Management</h2>
          <p style={{ margin: '0.4rem 0 0', color: '#666', fontSize: '0.9rem', maxWidth: 720 }}>
            Tune the AI Allocate algorithm. Changes persist globally and apply to the next AI Allocate run — no restart needed. Reset wipes your edits and restores the defaults.
          </p>
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={handleReset} disabled={saving} style={{ background: '#fff', color: '#c62828', border: '1px solid #ef9a9a', padding: '0.5rem 1rem', borderRadius: 6, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
              Reset to defaults
            </button>
            <button onClick={handleSave} disabled={saving || !dirty} style={{ background: dirty && !saving ? '#1565c0' : '#bdbdbd', color: 'white', border: 'none', padding: '0.5rem 1.25rem', borderRadius: 6, fontWeight: 600, cursor: dirty && !saving ? 'pointer' : 'not-allowed' }}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message" onClick={() => setError('')} title="Click to dismiss" style={{ cursor: 'pointer' }}>
          {error} <span style={{ opacity: 0.7, marginLeft: 8 }}>✕</span>
        </div>
      )}
      {success && (
        <div className="success-message" onClick={() => setSuccess('')} title="Click to dismiss" style={{ cursor: 'pointer' }}>
          {success} <span style={{ opacity: 0.7, marginLeft: 8 }}>✕</span>
        </div>
      )}

      {/* SCORING COMPONENTS */}
      <h3 style={{ marginTop: '1.25rem', marginBottom: '0.4rem' }}>Scoring rules</h3>
      <p style={{ margin: '0 0 0.6rem', color: '#666', fontSize: '0.85rem', maxWidth: 720 }}>
        Each rule contributes to the per-candidate score. Higher weight = stronger contribution. Toggle off to remove a rule entirely. Use the up/down arrows to set priority order — the algorithm sums every enabled rule, so the order is shown for clarity rather than computation.
      </p>
      <div style={{ border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden', marginBottom: '1.5rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
          <thead style={{ background: '#eceff1' }}>
            <tr>
              <th style={{ padding: '0.5rem', textAlign: 'left', width: 60 }}>Order</th>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Rule</th>
              <th style={{ padding: '0.5rem', textAlign: 'right', width: 110 }}>Weight</th>
              <th style={{ padding: '0.5rem', textAlign: 'center', width: 90 }}>Enabled</th>
            </tr>
          </thead>
          <tbody>
            {scoringSorted.map((s, vIdx) => {
              const oIdx = indexInOriginal(s.key);
              return (
                <tr key={s.key} style={{ borderBottom: '1px solid #f0f0f0', opacity: s.enabled ? 1 : 0.55 }}>
                  <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>
                    {canEdit && (
                      <>
                        <button
                          onClick={() => moveScoring(oIdx, -1)}
                          disabled={vIdx === 0}
                          title="Move up"
                          style={{ background: 'none', border: '1px solid #ddd', borderRadius: 3, cursor: vIdx === 0 ? 'not-allowed' : 'pointer', padding: '0.1rem 0.4rem', marginRight: 4 }}
                        >▲</button>
                        <button
                          onClick={() => moveScoring(oIdx, 1)}
                          disabled={vIdx === scoringSorted.length - 1}
                          title="Move down"
                          style={{ background: 'none', border: '1px solid #ddd', borderRadius: 3, cursor: vIdx === scoringSorted.length - 1 ? 'not-allowed' : 'pointer', padding: '0.1rem 0.4rem' }}
                        >▼</button>
                      </>
                    )}
                  </td>
                  <td style={{ padding: '0.4rem 0.5rem' }}>
                    <div style={{ fontWeight: 600 }}>{s.label}</div>
                    {s.description && <div style={{ fontSize: '0.75rem', color: '#666', marginTop: 2 }}>{s.description}</div>}
                  </td>
                  <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>
                    <input
                      type="number"
                      step="any"
                      value={s.value}
                      disabled={!canEdit || !s.enabled}
                      onChange={(e) => updateScoring(oIdx, { value: e.target.value === '' ? 0 : Number(e.target.value) })}
                      style={{ width: 90, padding: '0.3rem 0.4rem', textAlign: 'right', border: '1px solid #ccc', borderRadius: 3 }}
                    />
                  </td>
                  <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={!!s.enabled}
                      disabled={!canEdit}
                      onChange={(e) => updateScoring(oIdx, { enabled: e.target.checked })}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* LIMITS */}
      <h3 style={{ marginTop: '1.5rem', marginBottom: '0.4rem' }}>Limits & thresholds</h3>
      <p style={{ margin: '0 0 0.6rem', color: '#666', fontSize: '0.85rem', maxWidth: 760 }}>
        Structural rules. Changes apply on the next AI Allocate run. <strong>Note:</strong> the processing order (Championship first, then youngest → oldest) and the hard constraints (team-playing, no bench+ref overlap, must hold a paying qualification, trainees never auto-assigned) are fixed by the rule set and are not configurable here.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
        {[
          { key: 'defaultRefsPerGame',        label: 'Default referees per game',       help: 'Non-championship games. Default 2.', type: 'int', min: 0, max: 5 },
          { key: 'defaultBenchPerGame',       label: 'Default bench per game',          help: 'Non-championship games. Default 1.', type: 'int', min: 0, max: 5 },
          { key: 'championshipPeoplePerGame', label: 'Championship people per game',     help: 'Any division whose name contains "Championship" gets this many referees AND bench. Default 3.', type: 'int', min: 0, max: 6 },
          { key: 'playingConflictMinutes',    label: 'Playing-conflict window (min)',   help: 'A person whose assigned team plays within ±N minutes is excluded. Default 30.', type: 'int', min: 0, max: 240 },
          { key: 'priorMonthLookback',        label: 'Prior-month lookback (months)',   help: 'Calendar months back for the fairness average. Default 1.', type: 'int', min: 0, max: 12 },
          { key: 'historyLookbackMonths',     label: 'History lookback (months)',       help: 'Window for the historical-division match factor. Default 3.', type: 'int', min: 0, max: 24 },
        ].map(field => (
          <div key={field.key} style={{ background: '#fafafa', border: '1px solid #ddd', borderRadius: 6, padding: '0.6rem 0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>{field.label}</label>
            <input
              type="number"
              step={1}
              min={field.min}
              max={field.max}
              value={config.limits[field.key] ?? ''}
              disabled={!canEdit}
              onChange={(e) => updateLimit(field.key, e.target.value === '' ? 0 : Number(e.target.value))}
              style={{ width: '100%', padding: '0.35rem 0.5rem', border: '1px solid #ccc', borderRadius: 3, fontSize: '0.9rem' }}
            />
            <div style={{ fontSize: '0.72rem', color: '#666', marginTop: 4, lineHeight: 1.4 }}>{field.help}</div>
          </div>
        ))}

        {/* Championship senior-referee pattern */}
        <div style={{ background: '#fafafa', border: '1px solid #ddd', borderRadius: 6, padding: '0.6rem 0.75rem' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>Championship senior regex</label>
          <input
            type="text"
            value={config.limits.championshipSeniorRegex || ''}
            disabled={!canEdit}
            onChange={(e) => updateLimit('championshipSeniorRegex', e.target.value)}
            placeholder="advanced|veteran|national|state"
            style={{ width: '100%', padding: '0.35rem 0.5rem', border: '1px solid #ccc', borderRadius: 3, fontSize: '0.9rem', fontFamily: 'monospace' }}
          />
          <div style={{ fontSize: '0.72rem', color: '#666', marginTop: 4, lineHeight: 1.4 }}>
            Championship games must include ≥1 referee whose qualification level matches this (case-insensitive). Default <code>advanced|veteran|national|state</code> hits Advanced/Veterans 1, State Development, State Grading/Veterans 2, National — and excludes Community/Development/Intermediate/Trainee.
          </div>
        </div>

        {/* Trainee exclusion (boolean + regex) */}
        <div style={{ background: '#fafafa', border: '1px solid #ddd', borderRadius: 6, padding: '0.6rem 0.75rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>
            <input
              type="checkbox"
              checked={!!config.limits.excludeTrainees}
              disabled={!canEdit}
              onChange={(e) => updateLimit('excludeTrainees', e.target.checked)}
            />
            Exclude trainee qualifications
          </label>
          <div style={{ fontSize: '0.72rem', color: '#666', marginTop: 4, lineHeight: 1.4 }}>
            Trainee placement is a manual coaching call. When ticked, any qualification level matching the regex below is dropped from the candidate pool.
          </div>
        </div>

        <div style={{ background: '#fafafa', border: '1px solid #ddd', borderRadius: 6, padding: '0.6rem 0.75rem' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>Trainee regex</label>
          <input
            type="text"
            value={config.limits.traineeRegex || ''}
            disabled={!canEdit || !config.limits.excludeTrainees}
            onChange={(e) => updateLimit('traineeRegex', e.target.value)}
            placeholder="trainee"
            style={{ width: '100%', padding: '0.35rem 0.5rem', border: '1px solid #ccc', borderRadius: 3, fontSize: '0.9rem', fontFamily: 'monospace' }}
          />
          <div style={{ fontSize: '0.72rem', color: '#666', marginTop: 4, lineHeight: 1.4 }}>
            Case-insensitive. Default <code>trainee</code> matches "Trainee", "Trainee Bench", etc.
          </div>
        </div>
      </div>

      {/* Defaults reference for operator confidence */}
      {defaults && (
        <details style={{ marginTop: '1.25rem', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '0.5rem 0.75rem' }}>
          <summary style={{ cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: '#5d4037' }}>
            Show baked-in defaults
          </summary>
          <pre style={{ marginTop: '0.5rem', fontSize: '0.7rem', overflowX: 'auto', background: 'white', padding: '0.5rem', borderRadius: 3 }}>
{JSON.stringify(defaults, null, 2)}
          </pre>
        </details>
      )}

      {dirty && canEdit && (
        <div style={{ position: 'sticky', bottom: 8, marginTop: '1rem', textAlign: 'right' }}>
          <span style={{ background: '#fff3e0', color: '#e65100', padding: '0.4rem 0.75rem', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600 }}>
            Unsaved changes — click Save Changes above
          </span>
        </div>
      )}
    </div>
  );
}
